import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import Footer from '../../components/Footer';
import { 
  Wallet, 
  Coins, 
  Shield, 
  History, 
  Globe,
  CreditCard,
  ArrowRight,
  CheckCircle
} from 'lucide-react';

export default function DigiWalletPage() {
  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_mercamove/artifacts/ch8knfdf_Move_Logo_wHT.png" 
                alt="Mercapoli Move"
                className="h-8"
              />
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost" className="text-slate-700 hover:text-[#00BFA6]">
                  Iniciar Sesión
                </Button>
              </Link>
              <Link to="/register">
                <Button className="btn-primary px-6 py-2 text-sm">
                  Registrarse
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 bg-gradient-to-br from-[#00BFA6]/10 to-[#FFD166]/5">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#00BFA6]/10 rounded-full mb-6">
                <Wallet className="w-5 h-5 text-[#00BFA6]" />
                <span className="text-[#00BFA6] font-medium text-sm">Digi Wallet</span>
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 font-['Outfit'] leading-tight">
                Digi Wallet: Tu Billetera
                <span className="text-[#00BFA6]"> Digital Integrada</span>
              </h1>
              <p className="text-lg text-slate-600 mt-6 leading-relaxed">
                Digi Wallet es la billetera electrónica integrada en Mercapoli Move.
              </p>
              <p className="text-slate-600 mt-4">
                Paga servicios, recibe ingresos, administra transacciones y utiliza el token 
                utilitario DIGGII (1 DIGGII = 1 USD) de forma segura y transparente.
              </p>
              <Link to="/register">
                <Button className="btn-primary mt-8 h-14 px-8 text-lg">
                  Activar mi Wallet
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-[#00BFA6] to-[#00887A] rounded-3xl p-8 shadow-2xl text-white">
                <div className="flex items-center justify-between mb-8">
                  <Wallet className="w-10 h-10" />
                  <span className="text-white/80">Digi Wallet</span>
                </div>
                <p className="text-white/70 text-sm">Balance disponible</p>
                <p className="text-4xl font-bold mt-1">$1,250.00 USD</p>
                <div className="mt-6 pt-6 border-t border-white/20">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white/70 text-sm">Tokens DIGGII</p>
                      <p className="text-xl font-bold">150.00</p>
                    </div>
                    <Coins className="w-8 h-8 text-[#FFD166]" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 font-['Outfit'] text-center mb-12">
            Características Principales
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-slate-50 p-6 rounded-2xl">
              <div className="w-14 h-14 bg-[#00BFA6]/10 rounded-xl flex items-center justify-center mb-4">
                <Globe className="w-7 h-7 text-[#00BFA6]" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">Pagos en moneda local</h3>
              <p className="text-slate-600">Opera en tu moneda local con conversión automática transparente.</p>
            </div>
            <div className="bg-slate-50 p-6 rounded-2xl">
              <div className="w-14 h-14 bg-[#FFD166]/10 rounded-xl flex items-center justify-center mb-4">
                <Coins className="w-7 h-7 text-[#FFD166]" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">Token DIGGII</h3>
              <p className="text-slate-600">Usa tokens DIGGII (1:1 USD) para pagos, bonos y rewards.</p>
            </div>
            <div className="bg-slate-50 p-6 rounded-2xl">
              <div className="w-14 h-14 bg-[#00BFA6]/10 rounded-xl flex items-center justify-center mb-4">
                <History className="w-7 h-7 text-[#00BFA6]" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">Historial de transacciones</h3>
              <p className="text-slate-600">Accede al detalle completo de todos tus movimientos.</p>
            </div>
            <div className="bg-slate-50 p-6 rounded-2xl">
              <div className="w-14 h-14 bg-[#00887A]/10 rounded-xl flex items-center justify-center mb-4">
                <Shield className="w-7 h-7 text-[#00887A]" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">Seguridad financiera</h3>
              <p className="text-slate-600">Protección de nivel bancario para todas tus transacciones.</p>
            </div>
            <div className="bg-slate-50 p-6 rounded-2xl">
              <div className="w-14 h-14 bg-[#FFD166]/10 rounded-xl flex items-center justify-center mb-4">
                <CheckCircle className="w-7 h-7 text-[#FFD166]" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">Integración total</h3>
              <p className="text-slate-600">Una sola wallet para viajes, rentas, envíos y más.</p>
            </div>
          </div>
        </div>
      </section>

      {/* DIGGII Section */}
      <section className="py-16 px-4 bg-[#FFD166]/10">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-slate-900 font-['Outfit'] mb-6">
            ¿Qué es DIGGII?
          </h2>
          <p className="text-slate-600 text-lg mb-8">
            DIGGII es el token utilitario del ecosistema Mercapoli. Con valor estable 
            de 1 DIGGII = 1 USD, puedes usarlo para pagar servicios, recibir cashback 
            y obtener bonos por referidos.
          </p>
          <div className="grid sm:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-2xl shadow-lg">
              <Coins className="w-10 h-10 text-[#FFD166] mx-auto mb-3" />
              <p className="font-bold text-slate-800">1 DIGGII = 1 USD</p>
              <p className="text-slate-500 text-sm">Valor estable</p>
            </div>
            <div className="bg-white p-6 rounded-2xl shadow-lg">
              <CreditCard className="w-10 h-10 text-[#00BFA6] mx-auto mb-3" />
              <p className="font-bold text-slate-800">Paga servicios</p>
              <p className="text-slate-500 text-sm">Viajes, rentas, envíos</p>
            </div>
            <div className="bg-white p-6 rounded-2xl shadow-lg">
              <CheckCircle className="w-10 h-10 text-[#00887A] mx-auto mb-3" />
              <p className="font-bold text-slate-800">Gana bonos</p>
              <p className="text-slate-500 text-sm">Por referidos y uso</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
